import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import BrandingLogo from '../branding/BrandingLogo';

type Mode = 'password' | 'magic';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('password');

  // password mode
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // magic-link mode
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSent, setMagicSent] = useState(false);

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const ok = await login(username, password);
      if (ok) {
        navigate('/admin');
      } else {
        setError(t('auth.loginErrorDot'));
      }
    } catch (err) {
      setError(t('auth.loginErrorDot'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const resp = await apiService.requestMagicLink(magicEmail.trim());
      if (resp.data?.success) {
        setMagicSent(true);
      } else {
        setError(resp.data?.message || t('auth.requestFailed'));
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || t('auth.requestFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setError('');
    setMagicSent(false);
  };

  return (
    <div data-admin-shell data-admin-theme="mediterranean" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 via-gray-50 to-brand-accent/10 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Brand header */}
        <div className="text-center mb-8">
          <div className="mx-auto flex items-center justify-center">
            <BrandingLogo
              src="/api/settings/branding/logo"
              alt="SeaDent"
              className="h-20 w-auto max-w-[280px] object-contain"
              fallback={
                <div className="h-14 w-14 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-primary to-brand-primary-hover shadow-lg ring-2 ring-brand-accent/30 ring-offset-2 ring-offset-gray-100">
                  <span className="text-white text-2xl" aria-hidden>🦷</span>
                </div>
              }
            />
          </div>
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-brand-accent">
            {t('auth.tagline')}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md ring-1 ring-black/5 overflow-hidden">
          {/* Tabs */}
          <div className="grid grid-cols-2 border-b border-gray-200">
            <button
              type="button"
              onClick={() => switchMode('password')}
              className={`py-3 text-sm font-semibold transition-colors ${
                mode === 'password'
                  ? 'text-brand-primary border-b-2 border-brand-primary bg-brand-accent/5'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              🔑 {t('auth.password')}
            </button>
            <button
              type="button"
              onClick={() => switchMode('magic')}
              className={`py-3 text-sm font-semibold transition-colors ${
                mode === 'magic'
                  ? 'text-brand-primary border-b-2 border-brand-primary bg-brand-accent/5'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              ✨ {t('auth.magicLink')}
            </button>
          </div>

          <div className="p-7">
            {mode === 'password' && (
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label htmlFor="username" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    {t('auth.username')}
                  </label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    autoComplete="username"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                    placeholder="info@seadent.es"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    {t('auth.password')}
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                {error && <div className="text-red-600 text-sm">{error}</div>}

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2.5 px-4 rounded-md text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 transition-colors"
                >
                  {isLoading ? t('auth.loggingIn') : t('auth.signIn')}
                </button>
              </form>
            )}

            {mode === 'magic' && !magicSent && (
              <form onSubmit={handleMagicSubmit} className="space-y-4">
                <p className="text-sm text-gray-600 leading-relaxed">
                  <Trans i18nKey="auth.magicIntro" components={{ strong: <strong /> }} />
                </p>
                <div>
                  <label htmlFor="magicEmail" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    {t('auth.email')}
                  </label>
                  <input
                    id="magicEmail"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                    placeholder="info@seadent.es"
                    value={magicEmail}
                    onChange={(e) => setMagicEmail(e.target.value)}
                  />
                </div>

                {error && <div className="text-red-600 text-sm">{error}</div>}

                <button
                  type="submit"
                  disabled={isLoading || !magicEmail.trim()}
                  className="w-full py-2.5 px-4 rounded-md text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 transition-colors"
                >
                  {isLoading ? t('auth.sendingLink') : t('auth.sendLoginLink')}
                </button>
              </form>
            )}

            {mode === 'magic' && magicSent && (
              <div className="text-center py-2 space-y-3">
                <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-brand-accent/20 text-3xl">
                  📬
                </div>
                <h3 className="text-lg font-bold text-gray-900">{t('auth.mailSentTitle')}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t('auth.mailSentBody')}
                </p>
                <p className="text-xs text-gray-500 mt-3">
                  {t('auth.noMailPrefix')}{' '}
                  <button
                    type="button"
                    onClick={() => { setMagicSent(false); setError(''); }}
                    className="text-brand-primary font-semibold hover:underline"
                  >
                    {t('auth.requestAgain')}
                  </button>{t('auth.noMailSuffix')}
                </p>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-6">
          &copy; {new Date().getFullYear()} {t('auth.footer')}
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
